/**
 * ROLE CONTEXT
 * ============
 * Global context for managing user roles and permissions.
 * Fetches role from user_roles table on auth state change.
 * 
 * ROLE → MODULE ACCESS MAP:
 * - management: ALL modules (super-admin)
 * - doctor: vacation, self-service, shifts, messenger
 * - nurse: vacation, self-service, shifts, messenger
 * - driver: self-service, shifts, messenger
 * - procurement: procurement, warehouse, self-service, messenger
 * - sales: sales, self-service, messenger
 * - hr: hr, self-service, messenger
 * - operations: operations, daily-tasks, monthly-po, timesheets, fleet-tracker, fleet, self-service, messenger
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

export type AppRole = 'management' | 'nurse' | 'doctor' | 'procurement' | 'hr' | 'sales' | 'operations' | 'driver' | null;

/**
 * Defines which views each role can access.
 * Management has access to everything, so it's not listed here — handled via isManagement flag.
 */
const ROLE_ALLOWED_VIEWS: Record<Exclude<AppRole, 'management' | null>, string[]> = {
  doctor: ['vacation', 'self-service', 'shifts', 'messenger', 'profile'],
  nurse: ['vacation', 'self-service', 'shifts', 'messenger', 'profile'],
  driver: ['self-service', 'shifts', 'messenger', 'profile'],
  procurement: ['procurement', 'warehouse', 'self-service', 'messenger', 'profile'],
  sales: ['sales', 'self-service', 'messenger', 'profile'],
  hr: ['hr', 'self-service', 'messenger', 'profile'],
  operations: ['operations', 'daily-tasks', 'monthly-po', 'timesheets', 'fleet-tracker', 'fleet', 'self-service', 'messenger', 'profile'],
};

/** Default landing page per role */
const ROLE_DEFAULT_VIEW: Record<Exclude<AppRole, null>, string> = {
  management: 'dashboard',
  doctor: 'vacation',
  nurse: 'vacation',
  driver: 'self-service',
  procurement: 'procurement',
  sales: 'sales',
  hr: 'hr',
  operations: 'operations',
};

interface RoleContextType {
  role: AppRole;
  isLoading: boolean;
  isManagement: boolean;
  isNurse: boolean;
  isDoctor: boolean;
  isDriver: boolean;
  isProcurement: boolean;
  isSales: boolean;
  isHR: boolean;
  isOperations: boolean;
  canAccessView: (view: string) => boolean;
  getDefaultView: () => string;
  getAllowedViews: () => string[];
  refetchRole: () => Promise<void>;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export const RoleProvider: React.FC<{ children: React.ReactNode; user: User | null }> = ({ 
  children, 
  user 
}) => {
  const [role, setRole] = useState<AppRole>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchRole = useCallback(async () => {
    if (!user) {
      setRole(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .rpc('get_user_role', { _user_id: user.id });

      if (error) {
        console.error('Error fetching role:', error);
        setRole('nurse');
      } else {
        setRole(data as AppRole || 'nurse');
      }
    } catch (err) {
      console.error('Failed to fetch role:', err);
      setRole('nurse');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchRole();
  }, [fetchRole]);

  const canAccessView = useCallback((view: string): boolean => {
    if (!role) return false;
    if (role === 'management') return true;
    const allowed = ROLE_ALLOWED_VIEWS[role];
    return allowed ? allowed.includes(view) : false;
  }, [role]);

  const getDefaultView = useCallback((): string => {
    if (!role) return 'dashboard';
    return ROLE_DEFAULT_VIEW[role] || 'self-service';
  }, [role]);

  const getAllowedViews = useCallback((): string[] => {
    if (!role) return [];
    if (role === 'management') return []; // empty means all
    return ROLE_ALLOWED_VIEWS[role] || [];
  }, [role]);

  const value: RoleContextType = {
    role,
    isLoading,
    isManagement: role === 'management',
    isNurse: role === 'nurse',
    isDoctor: role === 'doctor',
    isDriver: role === 'driver',
    isProcurement: role === 'procurement',
    isSales: role === 'sales',
    isHR: role === 'hr',
    isOperations: role === 'operations',
    canAccessView,
    getDefaultView,
    getAllowedViews,
    refetchRole: fetchRole,
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = (): RoleContextType => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

export { ROLE_ALLOWED_VIEWS, ROLE_DEFAULT_VIEW };
export default RoleContext;
